/**
 * Forage bridge — the TapForage return contract (v1).
 *
 * TapForage's storefront opens the Drawer Builder with
 *
 *     ?forage_return=https://shop.tapforage.com/<seller>/<code>&forage_v=1
 *
 * In that mode "Add to cart" becomes "Continue with Forage": instead of
 * adding to this store's cart, the builder hands the build back to that page
 * as
 *
 *     <return>#forage_result=<base64url JSON>
 *     {"v":1,"function":"drawer-builder","build_id":"b_…",
 *      "lines":[{"variant_id":…,"quantity":…,"properties":{…}}],
 *      "attributes":{"__drawer_layout_b_…":"…","__drawer_build_uuid_b_…":"…"},
 *      "note":"Custom Drawer — …"}
 *
 * and Forage puts those lines through THIS store's Shopify checkout
 * (Storefront API cartCreate with the same line properties, cart attributes
 * and note), so the order, the orders/create webhook and the print pipeline
 * are unchanged.
 *
 * Every other visitor gets byte-for-byte today's behaviour: this file only
 * sets window.MD_FORAGE when the parameter is present and valid, and the
 * builder's add-to-cart handler consults it at exactly one point
 * (drawer-builder-legacy-inline.js, just before the theme-native add).
 *
 * Trust: the return address must be https on *.tapforage.com (no fragment,
 * no credentials, ≤ 512 chars). It is taken off the address bar straight
 * away — createURL() and mdBuildShareUrl() re-append location.search, so a
 * share link or a pushState must never carry it — and remembered in
 * sessionStorage for 30 minutes, honoured only across a same-origin round
 * trip (login, the AI layout page) or straight back from Forage.
 *
 * docs/forage-bridge.md has the contract and the test steps.
 */
(function (win) {
  'use strict';

  var STORAGE_KEY = 'md_forage_return';
  var MAX_AGE_MS = 30 * 60 * 1000;
  var MAX_URL_LEN = 512;
  var FORAGE_HOST_RE = /^([a-z0-9-]+\.)*tapforage\.com$/i;
  var LOCAL_HOSTS = ['localhost', '127.0.0.1'];
  var doc = win.document;

  function allowedOrigin(u) {
    if (u.protocol === 'https:' && FORAGE_HOST_RE.test(u.hostname)) return true;
    // A local storefront, only with the builder's own debug flag on.
    return u.protocol === 'http:' && LOCAL_HOSTS.indexOf(u.hostname) !== -1 && win.MD_DEBUG_BUILDER === true;
  }

  // The return address Forage may be sent back to, or null.
  function validReturnUrl(raw) {
    if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_URL_LEN) return null;
    var u;
    try { u = new URL(raw); } catch (e) { return null; }
    if (u.username || u.password || u.hash) return null;
    if (!allowedOrigin(u)) return null;
    // Origin + path only: Forage sends no query and none is needed.
    return u.origin + u.pathname;
  }

  // base64url (RFC 4648 §5, no padding) of a UTF-8 string.
  function encode(str) {
    var bytes = new TextEncoder().encode(str);
    var bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function clear() {
    try { win.sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage blocked */ }
  }

  function remember(returnUrl) {
    try {
      win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ returnUrl: returnUrl, at: Date.now() }));
    } catch (e) { /* storage blocked: the hand-off still works within this page */ }
  }

  function fromQuery() {
    var params;
    try { params = new URLSearchParams(win.location.search || ''); } catch (e) { return null; }
    if (!params.has('forage_return') && !params.has('forage_v')) return null;
    var ret = validReturnUrl(params.get('forage_return'));
    var version = params.get('forage_v');
    params.delete('forage_return');
    params.delete('forage_v');
    try {
      var qs = params.toString();
      win.history.replaceState(win.history.state, '', win.location.pathname + (qs ? '?' + qs : '') + win.location.hash);
    } catch (e) { /* sandboxed preview: leave the parameters in place */ }
    return ret && version === '1' ? ret : null;
  }

  function referrerHost() {
    try { return doc.referrer ? new URL(doc.referrer).hostname : ''; } catch (e) { return ''; }
  }

  function fromSession() {
    var raw = null;
    try { raw = win.sessionStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var rec = null;
    try { rec = JSON.parse(raw); } catch (e) { /* not ours */ }
    var ret = rec && typeof rec === 'object' ? validReturnUrl(rec.returnUrl) : null;
    if (!ret || Date.now() - Number(rec.at || 0) > MAX_AGE_MS) { clear(); return null; }
    var host = referrerHost();
    var sameOrigin = host !== '' && host === win.location.hostname;
    var fromForage = host !== '' && FORAGE_HOST_RE.test(host);
    if (!sameOrigin && !fromForage) { clear(); return null; }
    return ret;
  }

  function relabel() {
    var label = typeof win.MD_FORAGE_LABEL === 'string' && win.MD_FORAGE_LABEL ? win.MD_FORAGE_LABEL : 'Continue with Forage';
    var buttons = doc.querySelectorAll('.addToCart button');
    for (var i = 0; i < buttons.length; i++) {
      var span = buttons[i].querySelector('.db-topbar__cart-label') || buttons[i].querySelector('span');
      if (span && !(span.classList && span.classList.contains('totalPrice'))) span.textContent = label;
    }
    // The store's own cart is not part of this journey.
    var cartButtons = doc.querySelectorAll('.cart-drawer-button, [aria-controls="CartDrawer"]');
    for (var j = 0; j < cartButtons.length; j++) cartButtons[j].style.display = 'none';
  }

  function activate(returnUrl) {
    win.MD_FORAGE = { v: 1, returnUrl: returnUrl, function: 'drawer-builder' };
    remember(returnUrl);
    if (doc.documentElement && doc.documentElement.classList) doc.documentElement.classList.add('forage-mode');
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', relabel); else relabel();
  }

  // Hand a build back to Forage. Called by the add-to-cart handler in forage
  // mode; returns false (and does nothing) when there is nowhere to return to.
  function handoff(payload) {
    if (!win.MD_FORAGE || !win.MD_FORAGE.returnUrl) return false;
    var target;
    try { target = new URL(win.MD_FORAGE.returnUrl); } catch (e) { return false; }
    target.hash = 'forage_result=' + encode(JSON.stringify(payload));
    clear();
    win.location.assign(target.toString());
    return true;
  }

  win.MD_FORAGE_BRIDGE = {
    STORAGE_KEY: STORAGE_KEY,
    validReturnUrl: validReturnUrl,
    encode: encode,
    handoff: handoff
  };

  var returnUrl = fromQuery() || fromSession();
  if (returnUrl) activate(returnUrl);
})(typeof window !== 'undefined' ? window : this);
